import { useEffect, useState } from "react";

import { Avatar, Button, Icon } from "@web-app/components";

import AIIcon from "./icons/AIIcon";
import CubeIcon from "./icons/CubeIcon";
import PhoneIcon from "./icons/PhoneIcon";

interface ChatHeaderProps {
  canCall?: boolean;
  conversationType?: string;
  driverImage?: string;
  driverName: string;
  isAIConversation?: boolean;
  isEscalated?: boolean;
  isSearchMode?: boolean;
  isWingman?: boolean;
  onBack?: () => void;
  onClose?: () => void;
  onInitiateCall?: () => void;
  onMinimize?: () => void;
  onSearchChange?: (searchTerm: string) => void;
  onSearchToggle?: () => void;
  orderAppAvatarUrl?: string;
  orderDisplayId?: string;
  orderExternalId?: string;
  searchTerm?: string;
}

export default function ChatHeader({
  canCall = false,
  conversationType,
  driverImage,
  driverName,
  isAIConversation = false,
  isEscalated = false,
  isWingman = false,
  isSearchMode = false,
  onBack,
  onClose,
  onInitiateCall,
  onMinimize,
  onSearchChange,
  onSearchToggle,
  orderAppAvatarUrl,
  orderDisplayId,
  orderExternalId,
  searchTerm = "",
}: ChatHeaderProps) {
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);

  useEffect(() => {
    setLocalSearchTerm(searchTerm);
  }, [searchTerm]);

  const handleSearchChange = (value: string) => {
    setLocalSearchTerm(value);
    onSearchChange?.(value);
  };

  const handleCloseSearch = () => {
    setLocalSearchTerm("");
    onSearchChange?.("");
    onSearchToggle?.();
  };

  return (
    <div className="border-b border-gray-200 bg-white">
      <div className="flex items-center justify-between h-[50px] lg:h-[58px] px-3 lg:px-4">
        <div className="flex items-center gap-1.5 lg:gap-3">
          {/* Back button - only visible on mobile */}
          {onBack && (
            <Button
              onClick={onBack}
              className="lg:hidden !p-1 -ml-1 !rounded-full"
              plain
              aria-label="Back to conversations"
            >
              <Icon
                name="fasChevronLeft"
                className="w-3.5 h-3.5 lg:w-4 lg:h-4 text-gray-600"
              />
            </Button>
          )}
          {isAIConversation ? (
            <AIIcon className="w-7 h-7 lg:w-10 lg:h-10" />
          ) : (
            <Avatar image={driverImage || ""} name={driverName} size={6} />
          )}
          <div>
            <h3 className="text-xs lg:text-base font-semibold text-gray-900 truncate max-w-[120px] lg:max-w-[200px]">
              {isWingman
                ? "FULFLLD Wingman"
                : isAIConversation
                  ? "FULFLLD AI"
                  : driverName}
            </h3>
            {isAIConversation ? (
              <span className="flex items-center gap-1 text-[10px] lg:text-xs text-gray-500">
                {isEscalated ? "Support agent" : "AI Assistant"}
              </span>
            ) : (
              (orderExternalId || orderDisplayId) && (
                <span className="flex items-center gap-1 text-[10px] lg:text-xs text-gray-500">
                  {orderExternalId ? (
                    <>
                      {orderAppAvatarUrl && (
                        <img
                          src={orderAppAvatarUrl}
                          className="h-3 w-3 lg:h-3.5 lg:w-3.5"
                          alt=""
                        />
                      )}
                      {orderExternalId}
                    </>
                  ) : (
                    <>
                      <CubeIcon className="w-3 h-3 lg:w-3.5 lg:h-3.5" />
                      {orderDisplayId}
                    </>
                  )}
                </span>
              )
            )}
          </div>
        </div>
        <div className="flex items-center gap-0.5 lg:gap-1 -mr-1 lg:-mr-2">
          <Button
            onClick={onSearchToggle}
            className="hidden !p-1.5 lg:!p-2 !rounded-full"
            plain
            aria-label="Search in conversation"
          >
            <Icon
              name="magnifyingGlass"
              className="w-3.5 h-3.5 lg:w-4 lg:h-4 text-gray-600"
            />
          </Button>
          {!isAIConversation && (
            <Button
              aria-label={
                !canCall
                  ? "Start a conversation first"
                  : conversationType === "twilioFlex"
                    ? "Calling is not available for Wingman"
                    : "Start voice call"
              }
              className="!p-1 !rounded-full"
              disabled={!canCall || conversationType === "twilioFlex"}
              onClick={onInitiateCall}
              plain
              title={
                !canCall
                  ? "Start a conversation first"
                  : conversationType === "twilioFlex"
                    ? "Calling is not available for Wingman"
                    : "Start voice call"
              }
            >
              <PhoneIcon className="w-3.5 h-3.5 lg:w-4 lg:h-4" />
            </Button>
          )}
          {onMinimize && (
            <Button
              onClick={onMinimize}
              className="!w-5 !h-5 lg:!w-6 lg:!h-6 !p-0 !rounded-full"
              plain
              title="Minimize"
              aria-label="Minimize chat"
            >
              <Icon
                name="fasMinus"
                className="w-3.5 h-3.5 lg:w-4 lg:h-4 text-gray-600"
              />
            </Button>
          )}
          {onClose && (
            <Button
              onClick={onClose}
              className="hidden lg:flex !w-5 !h-5 lg:!w-6 lg:!h-6 !p-0 !rounded-full"
              plain
              aria-label="Close chat"
            >
              <Icon
                name="xMark"
                className="w-3.5 h-3.5 lg:w-4 lg:h-4 text-gray-600"
              />
            </Button>
          )}
        </div>
      </div>
      {isSearchMode && (
        <div className="px-3 lg:px-4 pb-2 lg:pb-3 pt-1">
          <div className="flex items-center gap-1.5 lg:gap-2 bg-gray-100 rounded-lg px-2 py-1.5 lg:px-3 lg:py-2">
            <Icon
              name="magnifyingGlass"
              className="w-3.5 h-3.5 lg:w-4 lg:h-4 text-gray-500"
            />
            <input
              type="text"
              value={localSearchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Search in conversation..."
              className="flex-1 bg-transparent focus:outline-none text-xs"
              autoFocus
            />
            {localSearchTerm && (
              <Button
                onClick={handleCloseSearch}
                className="!p-0.5 lg:!p-1 !rounded-full"
                plain
                aria-label="Clear search"
              >
                <Icon
                  name="xMark"
                  className="w-3.5 h-3.5 lg:w-4 lg:h-4 text-gray-600"
                />
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
